type RejectionEventLike = Pick<
  PromiseRejectionEvent,
  "reason" | "preventDefault" | "stopImmediatePropagation"
>;

type EventLike = Pick<
  Event,
  "preventDefault" | "stopImmediatePropagation"
>;

type ErrorTarget = {
  addEventListener: (
    type: string,
    listener: (event: any) => void,
    options?: boolean | AddEventListenerOptions,
  ) => void;
};

const extensionPrefixes = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "ms-browser-extension://",
  "extension://",
];

const extensionMessages = [
  "Object Not Found Matching Id",
  "The message port closed before a response was received",
  "message channel closed before a response was received",
  "Could not establish connection. Receiving end does not exist",
  "Extension context invalidated",
];

const abortMessages = [
  "The play() request was interrupted",
  "play() failed because the user didn't interact",
  "The operation was aborted",
  "Fetch is aborted",
  "The user aborted a request",
  "AbortError",
];

const litDomMethods = [
  "nextSibling",
  "insertBefore",
  "removeChild",
  "parentNode",
  "firstChild",
];

const hasExtensionPrefix = (text: string): boolean =>
  extensionPrefixes.some((prefix) => text.includes(prefix));

const extractStrings = (input: unknown): string[] => {
  if (typeof input === "string") return [input];
  if (typeof input !== "object" || input === null) return [];
  const obj = input as Record<string, unknown>;
  const keys = [
    "message",
    "stack",
    "filename",
    "fileName",
    "sourceURL",
    "name",
  ];
  return keys
    .map((k) => obj[k])
    .filter((v): v is string => typeof v === "string");
};

export const isEventRejection = (reason: unknown): boolean => {
  if (!reason || typeof reason !== "object") return false;
  if (reason instanceof Event) return true;
  if (typeof DOMException !== "undefined" && reason instanceof DOMException) {
    return true;
  }
  const tag = Object.prototype.toString.call(reason);
  if (tag.endsWith("Event]") || tag === "[object DOMException]") return true;
  const name = (reason as { constructor?: { name?: string } }).constructor
    ?.name;
  if (
    typeof name === "string" &&
    (name === "Event" || name.endsWith("Event") || name === "DOMException")
  ) {
    return true;
  }
  if (
    "isTrusted" in reason &&
    typeof (reason as { isTrusted: unknown }).isTrusted === "boolean"
  ) {
    return true;
  }
  const message = (reason as { message?: unknown }).message;
  if (typeof message === "string" && message.includes("IDBDatabase")) {
    return true;
  }
  return false;
};

export const isBrowserExtensionError = (reason: unknown): boolean =>
  extractStrings(reason).some(
    (str) =>
      hasExtensionPrefix(str) ||
      extensionMessages.some((msg) => str.includes(msg)),
  );

export const isExtensionHostObjectRejection = (reason: unknown): boolean =>
  isBrowserExtensionError(reason);

export const isMediaOrNetworkAbortError = (reasonOrEvent: unknown): boolean =>
  extractStrings(reasonOrEvent).some((str) =>
    abortMessages.some((msg) => str.includes(msg))
  );

export const isResizeObserverError = (reason: unknown): boolean =>
  extractStrings(reason).some((str) => str.includes("ResizeObserver"));

export const isNextSiblingNullError = (reason: unknown): boolean =>
  extractStrings(reason).some(
    (str) =>
      litDomMethods.some((method) => str.includes(method)) &&
      (str.includes("null") || str.includes("undefined")),
  );

export const isNullRejection = (reason: unknown): boolean =>
  reason === null || reason === undefined;

export const isScriptError = (event: unknown): boolean =>
  typeof ErrorEvent !== "undefined" &&
  event instanceof ErrorEvent &&
  event.message === "Script error." &&
  !event.error;

export const isNextSiblingNullErrorEvent = (event: unknown): boolean =>
  typeof ErrorEvent !== "undefined" &&
  event instanceof ErrorEvent &&
  (isNextSiblingNullError(event.error) ||
    isNextSiblingNullError(event.message));

export const isBrowserExtensionErrorEvent = (event: unknown): boolean =>
  typeof event === "object" &&
  event !== null &&
  (isBrowserExtensionError((event as { error?: unknown }).error) ||
    isBrowserExtensionError((event as { filename?: unknown }).filename) ||
    isBrowserExtensionError((event as { message?: unknown }).message));

export const isResizeObserverErrorEvent = (event: unknown): boolean =>
  typeof event === "object" &&
  event !== null &&
  (isResizeObserverError((event as { error?: unknown }).error) ||
    isResizeObserverError((event as { message?: unknown }).message));

export const isMediaOrNetworkAbortErrorEvent = (event: unknown): boolean =>
  typeof event === "object" &&
  event !== null &&
  (isMediaOrNetworkAbortError((event as { error?: unknown }).error) ||
    isMediaOrNetworkAbortError((event as { message?: unknown }).message));

// Swallows unhandledrejection storms from @instantdb/core's floating _gc()
// promise when IndexedDB fails (https://github.com/instantdb/instant/issues/2795),
// browser extension scripts / host object rejections, lit-html repeat directive
// nextSibling/DOM traversal null errors, media/network abort errors,
// ResizeObserver loop errors, and null promise rejections.

export const suppressEventRejection = (event: RejectionEventLike): void => {
  if (
    !isEventRejection(event.reason) &&
    !isBrowserExtensionError(event.reason) &&
    !isResizeObserverError(event.reason) &&
    !isNextSiblingNullError(event.reason) &&
    !isMediaOrNetworkAbortError(event.reason) &&
    !isNullRejection(event.reason)
  ) {
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
};

export const suppressScriptError = (event: EventLike): void => {
  if (
    !isScriptError(event) &&
    !isNextSiblingNullErrorEvent(event) &&
    !isBrowserExtensionErrorEvent(event) &&
    !isResizeObserverErrorEvent(event) &&
    !isMediaOrNetworkAbortErrorEvent(event)
  ) {
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
};

export const installEventRejectionSuppression = (
  target: ErrorTarget,
): void => {
  target.addEventListener(
    "unhandledrejection",
    (event) => suppressEventRejection(event),
    true,
  );
  target.addEventListener(
    "error",
    (event) => suppressScriptError(event),
    true,
  );
};
